import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, Observable, Observer, of, timer, Subscription } from 'rxjs';
import { switchMap, share, takeWhile, catchError, map, shareReplay } from 'rxjs/operators';

import { Instruction, Request, ProcessType, Validation } from '../main/models/request';

import { Status, State, StringResponse, isRunning } from '../main/models/status';
import { environment } from '../../environments/environment';
import { IFileInfo } from '../main/models/file-info';
import { ProcessStatus } from '../main/models/process-status';

/** GET /process/io/active: whether analyses can run at all (see jit-parent doc/IO-Worker.md). */
export interface IOStatus {
  /** The Temporal frontend answered. */
  temporalAvailable: boolean;
  /** The IO worker is polling STORE_TASK_QUEUE; always false when Temporal is not available. */
  active: boolean;
}

/**
 * What the process panel can offer:
 * - 'ok': the tools whose workers are running (an empty list means none);
 * - 'no-io': Temporal is up but the IO worker is not, so no analysis can download or upload;
 * - 'no-queue': Temporal itself (the analysis queue) cannot be reached, or the server did not answer.
 */
export type ToolAvailabilityStatus = 'ok' | 'no-io' | 'no-queue';

export interface ToolAvailability {
  status: ToolAvailabilityStatus;
  tools: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ProcessService {

  private static processNumber = 0;
  private INTERVAL = 5000; // in milliseconds
  private url: string;

  private readonly _activeToolNames$ = new BehaviorSubject<string[]>([]);
  private activeToolsFetched = false;

  /** How often the process panel re-checks the IO worker and tools, so it recovers without a reload. */
  private readonly AVAILABILITY_INTERVAL = 60000; // in milliseconds
  private toolAvailability$?: Observable<ToolAvailability>;

  constructor(private http: HttpClient) {
    this.url = environment.slideCropServer;
  }

  /** Cached observable of active tool names. Fetches once from the server, then replays. */
  getActiveToolNames$(): Observable<string[]> {
    if (!this.activeToolsFetched) {
      this.activeToolsFetched = true;
      this.activeTools().pipe(
        catchError(() => of([] as string[])),
      ).subscribe(tools => this._activeToolNames$.next(tools));
    }
    return this._activeToolNames$.asObservable();
  }

  private join(path: string): string {
    // environment.slideCropServer can be absolute (https://...) or relative (/api/)
    // Ensure exactly one slash between base and path
    const base = this.url.endsWith('/') ? this.url : `${this.url}/`;
    const p = path.startsWith('/') ? path.substring(1) : path;
    return `${base}${p}`;
  }

  public run(type: ProcessType, instr: Instruction) : Observable<StringResponse> {
    if (instr == null) throw new Error('A valid crops instruction must be provided!');
    if (instr.file == null) throw new Error('The file must not be null!');

    // Use POST to avoid URL length limits with large regions (e.g. wand-tool selections)
    ProcessService.processNumber++;
    instr.file.processNumber = ProcessService.processNumber;
    const url = new URL(this.join('process'), window.location.origin);
    url.searchParams.set('type', type);
    return this.http.post<StringResponse>(url.toString(), instr);
  }

  public validate(type: ProcessType, instr: Instruction) : Observable<Validation> {
    if (instr == null) throw new Error('A valid instruction is required for process validation');
    if (instr.file == null) throw new Error('A file is required for process validation');

    // Use POST to avoid URL length limits with large regions (e.g. wand-tool selections)
    const url = new URL(this.join('validate'), window.location.origin);
    url.searchParams.set('type', type);
    return this.http.post<Validation>(url.toString(), instr);
  }

  public createMonitorUrl(workflowId: string): URL {
    const queryParams = { workflowId: workflowId };
    const url = new URL(this.join('status'), window.location.origin);
    url.search = new URLSearchParams(queryParams as any).toString();
    return url;
  }

  /**
   * The status URL to poll for a submitted workflow, from whatever `/api/process`
   * answered with — a bare workflowId or a full status URL.
   *
   * `run()`'s response is an ABSOLUTE status URL built from the *server's* own
   * hostname, and polling that raw only works while the browser's origin happens
   * to match it. Under `ng serve` the app is `localhost:4200` with `/api` proxied
   * same-origin, so an absolute `https://imagetools-dev.jax.org/api/status`
   * becomes a cross-origin request — and the ingress oauth2-proxy answers its
   * preflight with a 302, which is a hard CORS failure ("Redirect is not allowed
   * for a preflight request"). The poll then never runs, so the workflow's result
   * never reaches its observer: locally, Find appeared to return no regions at
   * all (jit-ui#124 investigation).
   *
   * So keep only the workflowId and rebuild the URL against THIS app's API base,
   * which is what every other monitor caller already does via
   * {@link createMonitorUrl}.
   */
  public monitorUrlFor(idOrUrl: string): URL {
    return this.createMonitorUrl(this.extractWorkflowId(idOrUrl));
  }

  /** The `workflowId` query parameter when handed a status URL; the string
   *  unchanged when handed a bare id. */
  private extractWorkflowId(idOrUrl: string): string {
    try {
      // A bare id resolves as a relative path and simply has no query string,
      // so one branch covers both shapes.
      return new URL(idOrUrl, window.location.origin).searchParams.get('workflowId') ?? idOrUrl;
    } catch {
      return idOrUrl;
    }
  }

  /**
   Monitor a given url for each time it returns status.
   Look for a status which is final such as COMPLETE or ERROR
   and then stop monitoring.
   */
  public monitor(url: URL, observer: Observer<Status>) : Subscription {
    // From this https://blog.angulartraining.com/how-to-do-polling-with-rxjs-and-angular-50d635574965
    // We try to poll every internal and stop when state is not running.
    const events : Observable<Status> = timer(0, this.INTERVAL).pipe(
      switchMap(()=>{
        return this.http.get<Status>(url.toString()).pipe(
          catchError((error) => {
            console.error('Status polling error:', error.status, error.message);
            return of({ state: State.ERROR, message: `Status check failed: ${error.status}` } as Status);
          })
        );
      }),
      share(),
      takeWhile((status:Status)=>{
        // A non status might just mean not started yet.
        if (!status) {
          return true;
        } else {
          const state: State | undefined = (status) ? status.state : undefined;
          if (state) {
            const busy: boolean = isRunning(state);
            return busy;
          } else {
            return true;
          }
        }
      }, true));

    return events.subscribe(observer);
  }

  public getDefaultRequest(type: ProcessType, file: IFileInfo) : Observable<Request> {
    let path = this.url + 'process/default?type=' + type;
    if (file != null) {
      const base64: string = this.encode(file);
      path = path + '&info=' + base64;
    }
    return this.http.get<Request>(path);
  }

  public encode(file: IFileInfo): string {
    const json: string = JSON.stringify(file);
    return btoa(json);
  }

  public getStatusByUserName(userName: string) {
    const queryParams = { userName: userName };
    const url = new URL(this.join('status-by-user'), window.location.origin);
    url.search = new URLSearchParams(queryParams as any).toString();
    return this.http.get<ProcessStatus[]>(url.toString());
  }

  public terminateProcess(workflowId: string) {
    const queryParams = { workflowId: workflowId };
    const url = new URL(this.join('terminate'), window.location.origin);
    url.search = new URLSearchParams(queryParams as any).toString();
    return this.http.get(url.toString());
  }

	public activeTools(): Observable<string[]> {
	  return this.http.get<string[]>(this.join('process/tools/active'));
	}

	public ioStatus(): Observable<IOStatus> {
	  return this.http.get<IOStatus>(this.join('process/io/active'));
	}

	/**
	 * The process panel's tools, checked now and every AVAILABILITY_INTERVAL while anyone is
	 * subscribed. Unlike getActiveToolNames$(), an empty list here means no tools.
	 */
	public getToolAvailability$(): Observable<ToolAvailability> {
	  if (!this.toolAvailability$) {
	    this.toolAvailability$ = timer(0, this.AVAILABILITY_INTERVAL).pipe(
	      switchMap(() => this.fetchToolAvailability()),
	      shareReplay({ bufferSize: 1, refCount: true }),
	    );
	  }
	  return this.toolAvailability$;
	}

	private fetchToolAvailability(): Observable<ToolAvailability> {
	  return this.ioStatus().pipe(
	    switchMap((io): Observable<ToolAvailability> => {
	      if (!io?.temporalAvailable) return of({ status: 'no-queue', tools: [] });
	      if (!io.active) return of({ status: 'no-io', tools: [] });
	      return this.activeTools().pipe(
	        map((tools): ToolAvailability => ({ status: 'ok', tools: tools ?? [] })),
	      );
	    }),
	    // The server not answering is treated as the queue not being available.
	    catchError(() => of<ToolAvailability>({ status: 'no-queue', tools: [] })),
	  );
	}

}